import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../Layouts/DashboardLayout';
import ReviewCard from '../ReviewsManagement/ReviewCard';
import GMBService from '../../services/GMBService';

import logger from '../../utils/logger';
const PageContainer = styled(Stack)(({ theme }) => ({
  gap: '40px',
  width: '100%'
}));

const PageHeader = styled(Stack)(({ theme }) => ({
  gap: '16px'
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '28px',
  fontWeight: 600,
  color: '#0B91D6'
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  color: '#30302E'
}));

const ReviewsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: '40px',
  width: '100%',
  maxWidth: '1200px'
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px'
}));

const NoReviewsText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  color: '#666666',
  textAlign: 'center',
  padding: '40px'
}));

const ReviewItem = styled(Box)(({ theme }) => ({
  transition: 'all 0.2s ease-in-out',
  borderRadius: '8px',
  padding: '16px',
  '&:hover': {
    backgroundColor: '#f5f5f5'
  }
}));

const CreatePostPage = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch reviews from GMB API
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        setError('');
        const account = JSON.parse(localStorage.getItem('selectedGMBAccount') || 'null');
        const location = JSON.parse(localStorage.getItem('selectedGMBLocation') || 'null');
        
        if (!account?.name || !location?.name) {
          setReviews([]);
          setLoading(false);
          return;
        }
        
        const apiReviews = await GMBService.getReviews(undefined, account.name, location.name);
        const apiArray = Array.isArray(apiReviews) ? apiReviews : (apiReviews?.reviews || []);
        
        // Map to ReviewCard shape
        const toNumeric = (star) => {
          if (typeof star === 'number') return star;
          const map = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
          return map[String(star || '').toUpperCase()] || 0;
        };
        
        const mapped = apiArray.map((r, idx) => ({
          id: r.name || idx,
          author: r.reviewer?.displayName || 'Anonymous',
          date: r.createTime ? new Date(r.createTime).toLocaleDateString() : 'Recent',
          createdAt: r.createTime ? new Date(r.createTime).getTime() : Date.now(),
          rating: toNumeric(r.starRating),
          platform: 'Google',
          text: r.comment || 'No comment provided',
          avatar: r.reviewer?.profilePhotoUrl || '/images/karen-avatar.jpg'
        }));
        
        setReviews(mapped);
      } catch (e) {
        logger.error('Failed to load reviews:', e);
        setError('Failed to load reviews');
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReviews();
  }, []);

  const handleReviewClick = (review) => {
    // Store selected review data for post generation
    localStorage.setItem('selectedReviewForPost', JSON.stringify(review));
    // Navigate directly to post generation results page (skip font selection)
    navigate('/post-generation-results');
  };

  return (
    <DashboardLayout>
      <PageContainer>
        <PageHeader>
          <PageTitle>Create Post</PageTitle>
          <PageSubtitle>
            Select a review to create an engaging testimonial post for your Google My Business profile.
          </PageSubtitle>
        </PageHeader>

        <ReviewsContainer>
          {loading ? (
            <LoadingContainer>
              <CircularProgress size={40} sx={{ color: '#0B91D6' }} />
            </LoadingContainer>
          ) : error ? (
            <NoReviewsText>
              {error}
            </NoReviewsText>
          ) : reviews.length === 0 ? (
            <NoReviewsText>
              No reviews available. Please connect your Google My Business account to see reviews.
            </NoReviewsText>
          ) : (
            <Stack gap="24px">
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#121927',
                  marginBottom: '16px'
                }}
              >
                Click on "Create Post" button to generate a testimonial post:
              </Typography>
              {reviews.map((review) => (
                <ReviewItem key={review.id}>
                  <ReviewCard
                    review={review}
                    selected={false}
                    onSelect={() => {}}
                    onAIReply={() => {}}
                    onCreatePost={() => {
                      logger.log('[CreatePostPage] Create Post clicked for review:', review.id);
                      handleReviewClick(review);
                    }}
                  />
                </ReviewItem>
              ))}
            </Stack>
          )}
        </ReviewsContainer>
      </PageContainer>
    </DashboardLayout>
  );
};

export default CreatePostPage;